import { NIM } from '@yxim/nim-web-sdk'

import { chatConfig } from '@/const/chatConfig'
import { getOrderedSessions } from '@/util/chatUtil'
import { MessagePlugin } from 'tdesign-vue-next'
import { defineStore } from 'pinia'

const useNIMStore = defineStore('nim', {
  state: () => {
    return {
      nim: null,
      sessionList: [],
      sessionMap: {},
      currentUserAccount: null,
      syncDone: false, // 初始化同步是否完成（onsyncdone）
      unreadTotal: null, // 会话未读总数，同步完成后以服务端数据为准
    }
  },
  getters: {
    // 问答应用(chatQa)红点数：同步完成前不展示，避免本地 IndexedDB 缓存的过期未读数导致红点误报
    qaUnread(state) {
      if (!state.syncDone) return 0
      if (state.unreadTotal !== null && state.unreadTotal !== undefined) {
        return state.unreadTotal
      }
      return state.sessionList.reduce((sum, item) => sum + (item.unread || 0), 0)
    },
  },
  actions: {
    // 登录chat
    loginChat(account, token) {
      if (this.nim) {
        // 账号变化时必须销毁旧实例，沿用上一账号的连接会导致未读红点错乱
        if (this.currentUserAccount && this.currentUserAccount !== account) {
          this.logoutChat()
        } else {
          return
        }
      }
      if (account && token) {
        this.initChat(account, token)
      } else {
        MessagePlugin.error('当前用户没有云信账号或密码,请联系管理员')
      }
    },
    logoutChat() {
      if (this.nim) {
        try {
          this.nim.disconnect()
          this.nim.destroy()
        } catch (e) {
          console.log('nim 实例销毁失败', e)
        }
        this.nim = null
      }
      this.resetSessions()
    },
    // 清空会话及未读相关状态，避免残留过期红点
    resetSessions() {
      this.sessionList = []
      this.sessionMap = {}
      this.syncDone = false
      this.unreadTotal = null
    },
    initChat(account, token) {
      this.currentUserAccount = account
      this.resetSessions()
      this.nim = NIM.getInstance({
        debug: false,
        appKey: chatConfig.test.appKey,
        account: account,
        token: token,
        db: true,
        authType: 0, // token的认证类型，0：token是固定的， 只有主动调用接口修改才会更改，1：token是动态的，有过期事件，token过期后已登录状态的链接不受影响，但是之后的登录需要信token，对于自动重连场景，开发者可在onwillreconnect中，判断token是否过期，若过期可调用setOptions更新token
        syncSessionUnread: true, // 是否同步会话的未读数, 默认不同步
        rollbackDelMsgUnread: true, // 是否回滚删除的消息的未读数，默认不同步 比如有两条未读消息。消息发送方撤回了其中一条。如果该参数为 false，则撤回后，未读数为2。如果该参数为 true，则撤回后，未读数为1
        reconnectionAttempts: 3, // 自动重连次数
        customClientType: 10, // 自定义客户端类型，大于0的整数；可实现自定义多端登录策略，具体策略可在云信管理后台配置
        onwillreconnect: this.onWillReconnect,
        ondisconnect: this.onDisconnect,
        onerror: this.onerror,
        // 会话
        onsessions: this.onSessions,
        onupdatesessions: this.onUpdateSessions,
        onsyncdone: this.onSyncDone,
      })
      this.nim.connect()
    },
    onWillReconnect(obj) {
      // 此时说明 SDK 已经断开连接, 请开发者在界面上提示用户连接已断开, 而且正在重新建立连接
      console.log('即将重连', obj)
    },
    onDisconnect(error) {
      // 此时说明 SDK 处于断开状态, 开发者此时应该根据错误码提示相应的错误信息, 并且跳转到登录页面
      console.log('nim 连接断开', error)
      if (error) {
        switch (error.code) {
          // 302: 账号或密码错误, 417: 重复登录, 'kicked': 被踢
          // 连接不可恢复：清空本地会话数据，避免过期未读数继续显示红点
          case 302:
          case 417:
          case 'kicked':
            this.resetSessions()
            break
          default:
            break
        }
      }
    },
    onerror(error) {
      console.log('nim 错误', error)
    },
    /**
     * 初始化阶段收到会话列表
     * 该回调函数返回的会话不包含isTop属性
     */
    onSessions(sessions) {
      console.log('onSessions', sessions)
      this.sessionMap = {}
      for (const session of sessions) {
        this.sessionMap[session.id] = session
      }
      this.sessionList = getOrderedSessions(this.sessionMap)
      this.refreshUnreadTotal()
    },
    /**
     * 会话更新，sessionArr中的msgReceiptTime属性为对端最后一条已读回执的事件
     * 根据msgReceiptTime，在渲染段判断每条消息是否已读
     */
    onUpdateSessions(sessions) {
      console.log('会话更新了', sessions)
      for (const session of sessions) {
        this.sessionMap[session.id] = session
      }
      this.sessionList = getOrderedSessions(this.sessionMap)
      this.refreshUnreadTotal()
      // 标记会话中每条消息的已读回执是否收到
    },
    refreshUnreadTotal() {
      this.unreadTotal = this.sessionList.reduce((sum, item) => sum + (item.unread || 0), 0)
    },
    // 初始化同步完成后，标记状态并以服务端同步结果校准未读总数
    async onSyncDone() {
      this.syncDone = true
      // 官方文档要求 db 相关操作需在 onsyncdone 结束后再调用
      if (this.nim && typeof this.nim.getUnreadTotal === 'function') {
        this.nim.getUnreadTotal({
          done: (error, total) => {
            if (!error) {
              const num = typeof total === 'number' ? total : total && total.unread
              this.unreadTotal = Number(num) || 0
            }
          },
        })
      }
    },
  },
})

export default useNIMStore
